// src/pages/HomePage.jsx
import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { track } from "../utils/track";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import {
  ArrowRight, Clock, DollarSign, Sparkles, Zap,
  ShoppingCart, Utensils, Stethoscope, Home as HomeIcon,
  Code, Calendar, Shield, Star, CheckCircle, Lock,
  ChevronRight, Brain, X, Play, Check, Search, Radar,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LanguageContext } from "../App";

// ─── constants ───────────────────────────────────────────────────────────────

const SOCIAL_PROOF_AVATARS = [
  { initials: "SM", bg: "bg-blue-600" },
  { initials: "MT", bg: "bg-purple-600" },
  { initials: "DL", bg: "bg-blue-500" },
  { initials: "JK", bg: "bg-orange-500" },
  { initials: "RB", bg: "bg-pink-600" },
];

const buildStats = (toolCount, price, t) => [
  { id: "tools",      value: `${toolCount}+`,   label: t("home.statsToolsLabel"),       color: "text-[#3b82f6]" },
  { id: "timeline",   value: "12–16 wks",        label: t("home.statsTimelineLabel"),    color: "text-purple-400"  },
  { id: "industries", value: "12",                label: t("home.statsIndustriesLabel"),  color: "text-blue-400" },
  { id: "price",      value: `$${price}`,         label: t("home.statsPriceLabel"),       color: "text-[#3b82f6]" },
];

const buildWhatYouGet = (t, toolCount) => [
  { icon: Calendar,   title: t("home.wyg1Title"), desc: t("home.wyg1Desc"), stat: t("home.wyg1Stat"), color: "text-blue-400",    glow: "from-blue-500/20",    border: "border-blue-500/20"    },
  { icon: Zap,        title: t("home.wyg2Title", { toolCount }), desc: t("home.wyg2Desc"), stat: t("home.wyg2Stat"), color: "text-purple-400",  glow: "from-purple-500/20",  border: "border-purple-500/20", featured: true },
  { icon: Sparkles,   title: t("home.wyg3Title"), desc: t("home.wyg3Desc"), stat: t("home.wyg3Stat"), color: "text-[#3b82f6]", glow: "from-blue-500/20", border: "border-blue-500/20" },
  { icon: DollarSign, title: t("home.wyg4Title"), desc: t("home.wyg4Desc"), stat: t("home.wyg4Stat"), color: "text-yellow-400",  glow: "from-yellow-500/20",  border: "border-yellow-500/20"  },
  { icon: Clock,      title: t("home.wyg5Title"), desc: t("home.wyg5Desc"), stat: t("home.wyg5Stat"), color: "text-blue-400",    glow: "from-blue-500/20",    border: "border-blue-500/20"    },
  { icon: Shield,     title: t("home.wyg6Title"), desc: t("home.wyg6Desc"), stat: t("home.wyg6Stat"), color: "text-[#3b82f6]", glow: "from-blue-500/20", border: "border-blue-500/20" },
];


const buildComparisonRows = (t) => [
  { feature: t("home.cmpRow1"), us: true,   consultant: true,                         diy: false },
  { feature: t("home.cmpRow2"), us: true,   consultant: t("home.cmpSometimes"),        diy: false },
  { feature: t("home.cmpRow3"), us: true,   consultant: false,                         diy: false },
  { feature: t("home.cmpRow4"), us: true,   consultant: false,                         diy: false },
  { feature: t("home.cmpRow5"), us: true,   consultant: false,                         diy: false },
  { feature: t("home.cmpRow6"), us: true,   consultant: true,                          diy: false },
  { feature: t("home.cmpRowCost"), us: null,   consultant: "$5,000+",                  diy: t("home.cmpDiyHours") },
];


const PRICING_ITEMS = [
  "pricingItem1","pricingItem2","pricingItem3",
  "pricingItem4","pricingItem5","pricingItem6",
];

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const fadeUp  = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

// ─── Blueprint Preview ────────────────────────────────────────────────────────
const BlueprintPreview = ({ t }) => (
  <div className="relative select-none">
    {/* Outer glow */}
    <div className="absolute -inset-6 bg-gradient-to-br from-blue-500/15 via-blue-500/10 to-purple-500/15 rounded-[3rem] blur-3xl" />
    <div className="relative rounded-3xl overflow-hidden bg-slate-900/80 backdrop-blur-xl border border-white/10 shadow-2xl">
      {/* Top bar */}
      <div className="bg-slate-950/60 px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-[#3b82f6] font-semibold tracking-[0.2em] uppercase mb-0.5 font-['Space_Grotesk']">{t("home.mockupBlueprintLabel")}</div>
          <div className="text-sm font-bold text-white font-['Space_Grotesk']">{t("home.mockupStoreLabel")}</div>
        </div>
        <span className="text-[10px] font-semibold bg-blue-500/15 text-[#3b82f6] border border-blue-500/25 px-3 py-1.5 rounded-full">
          ✓ {t("home.mockupReportReady")}
        </span>
      </div>

      {/* ROI bar — range, not a single aspirational number. An FTC-flavored
          "$42K" on the hero while most buyers see $7–20K in their actual
          report is how refund tickets are born. Show the realistic floor
          alongside the top-decile ceiling so the promise matches the delivery. */}
      <div className="grid grid-cols-3 border-b border-white/5">
        {[["$7–42K", t("home.mockupYear1Roi")], ["6–12 hrs", t("home.mockupSavedWeek")], ["5–7 tools", t("home.mockupRecommended")]].map(([val, lab]) => (
          <div key={lab} className="px-4 py-3.5 text-center border-r last:border-r-0 border-white/5">
            <div className="text-base font-bold text-[#3b82f6] font-['Space_Grotesk']">{val}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{lab}</div>
          </div>
        ))}
      </div>

      {/* Tool match list */}
      <div className="p-5 space-y-2.5">
        <div className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-semibold mb-3">{t("home.mockupTop3Tools")}</div>
        {[
          { name: t("home.mockupTool1Name"), badge: t("home.mockupTool1Badge"), score: 94, color: "bg-blue-500" },
          { name: t("home.mockupTool2Name"), badge: t("home.mockupTool2Badge"), score: 87, color: "bg-purple-500" },
          { name: t("home.mockupTool3Name"), badge: t("home.mockupTool3Badge"), score: 91, color: "bg-blue-500" },
        ].map((tool) => (
          <div key={tool.name} className="bg-slate-950/50 rounded-2xl p-3.5 border border-white/5">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-white">{tool.name}</span>
              <span className="text-[10px] text-slate-400">{tool.badge}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full ${tool.color} rounded-full`}
                  initial={{ width: 0 }}
                  animate={{ width: `${tool.score}%` }}
                  transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }}
                />
              </div>
              <span className="text-[10px] text-slate-500 flex-shrink-0">{tool.score}% {t("home.mockupMatch")}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Progress */}
      <div className="px-5 pb-5">
        <div className="bg-slate-950/50 rounded-2xl p-3.5 border border-white/5">
          <div className="flex justify-between text-[10px] mb-2">
            <span className="text-slate-400 font-medium">{t("home.mockupImplProgress")}</span>
            <span className="text-[#3b82f6] font-semibold">{t("home.mockupProgressDone")}</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: "16%" }}
              transition={{ duration: 1.5, delay: 0.8, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>

      {/* Phase roadmap */}
      <div className="px-5 pb-5">
        <div className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-semibold mb-2.5">{t("home.mockup90DayRoadmap")}</div>
        <div className="flex gap-2">
          {[t("home.mockupPhase1"), t("home.mockupPhase2"), t("home.mockupPhase3")].map((p, i) => (
            <div key={i} className={`flex-1 text-center rounded-xl py-2.5 px-1 text-[10px] font-medium border ${
              i === 0 ? "bg-blue-500/15 border-blue-500/30 text-blue-400" : "bg-slate-900/50 border-white/5 text-slate-600"
            }`}>
              {p.split("\n").map((line, j) => <div key={j}>{line}</div>)}
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Floating badges */}
    <motion.div
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      className="absolute -top-4 -right-3 bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-blue-500/40"
    >
      ⚡ {t("home.mockupGeneratedBadge")}
    </motion.div>
    <motion.div
      animate={{ y: [0, 6, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      className="absolute -bottom-3 -left-3 bg-gradient-to-br from-purple-500 to-purple-700 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-purple-500/40"
    >
      348+ {t("home.mockupToolsEvaluated")}
    </motion.div>
  </div>
);

// ─── component ───────────────────────────────────────────────────────────────

const HomePage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");

  const [toolCount, setToolCount] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(149);
  const [newArrivals, setNewArrivals] = useState([]);
  const [giveaway, setGiveaway] = useState(null);
  const [giveawayEmail, setGiveawayEmail] = useState("");
  const [giveawayLoading, setGiveawayLoading] = useState(false);
  const [giveawayClaimed, setGiveawayClaimed] = useState(false);
  const { t, lang } = useContext(LanguageContext);

  const displayCount   = toolCount || 348;
  const STATS          = buildStats(displayCount, currentPrice, t);
  const WHAT_YOU_GET   = buildWhatYouGet(t, displayCount);

  const COMPARISON_ROWS = buildComparisonRows(t);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com";
    fetch(`${base}/api/tools/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});
    fetch(`${base}/api/tools/new-arrivals?limit=12`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.tools?.length) setNewArrivals(d.tools); })
      .catch(() => {});
    fetch(`${base}/api/pricing/current`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && typeof d.price === 'number') setCurrentPrice(d.price); })
      .catch(() => {});
    fetch(`${base}/api/giveaway/status`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setGiveaway(d); })
      .catch(() => {});
  }, []);


  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) { toast.error(t("home.subscribeErrorEmail")); return; }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com"}/api/subscribe`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) }
      );
      if (!res.ok) throw new Error();
      toast.success(t("home.subscribeSuccess"));
      setEmail("");
    } catch {
      toast.error(t("home.subscribeError"));
    }
  };

  const handleClaimGiveaway = async (e) => {
    e.preventDefault();
    if (!giveawayEmail || !giveawayEmail.includes('@')) { toast.error("Enter a valid email"); return; }
    setGiveawayLoading(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com";
      const res = await fetch(`${base}/api/giveaway/claim`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: giveawayEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setGiveawayClaimed(true);
        setGiveaway(prev => prev ? { ...prev, remaining: Math.max(0, prev.remaining - 1), claimed: prev.claimed + 1 } : prev);
        toast.success(data.message || "Check your email for the code!");
      } else {
        toast.error(data.detail || "Could not claim — spots may be gone");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setGiveawayLoading(false);
    }
  };

  const _half       = Math.ceil(newArrivals.length / 2);
  const marqueeRow1 = newArrivals.slice(0, _half);
  const marqueeRow2 = newArrivals.slice(_half).length >= 2 ? newArrivals.slice(_half) : [...newArrivals].reverse().slice(0, _half);
  const arrivalDateCounts = newArrivals.reduce((acc, tool) => {
    acc[tool.date_added] = (acc[tool.date_added] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0f172a] text-white overflow-x-hidden">
      <Helmet>
        <html lang={lang} />
        <title>{t("home.metaTitle")}</title>
        <meta name="description" content={t("home.metaDescription")} />
        <link rel="canonical" href={lang === "es" ? "https://www.aiworkshopllc.com/es" : lang === "tr" ? "https://www.aiworkshopllc.com/tr" : "https://www.aiworkshopllc.com/"} />
        <link rel="alternate" hrefLang="en" href="https://www.aiworkshopllc.com/" />
        <link rel="alternate" hrefLang="es" href="https://www.aiworkshopllc.com/es" />
        <link rel="alternate" hrefLang="tr" href="https://www.aiworkshopllc.com/tr" />
        <link rel="alternate" hrefLang="x-default" href="https://www.aiworkshopllc.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.aiworkshopllc.com/" />
        <meta property="og:title" content={t("home.metaTitle")} />
        <meta property="og:description" content={t("home.metaDescription")} />
        <meta property="og:image" content="https://www.aiworkshopllc.com/android-chrome-512x512.png" />
        <meta property="og:site_name" content="AI Workshop LLC" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("home.metaTitle")} />
        <meta name="twitter:description" content={t("home.metaDescription")} />
        <meta name="twitter:image" content="https://www.aiworkshopllc.com/android-chrome-512x512.png" />
      </Helmet>
      <Header />

      {/* ── EMAIL CAPTURE BANNER ─────────────────────────────────────────────── */}
      {!giveawayClaimed && (
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="sticky top-16 z-40 bg-gradient-to-r from-blue-600/95 via-blue-500/95 to-purple-600/95 backdrop-blur-md border-b border-white/10 shadow-lg shadow-blue-500/20"
        >
          <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <span className="text-2xl">🚀</span>
              <div>
                <p className="text-white font-bold text-sm sm:text-base">
                  Get weekly AI tool picks for your industry — free
                </p>
                <p className="text-blue-100/80 text-xs">
                  Join our list. First subscribers get an exclusive code for a free AI implementation blueprint.
                </p>
              </div>
            </div>
            <form onSubmit={handleClaimGiveaway} className="flex items-center gap-2 w-full sm:w-auto flex-shrink-0">
              <input
                type="email"
                placeholder="you@email.com"
                value={giveawayEmail}
                onChange={(e) => setGiveawayEmail(e.target.value)}
                className="w-full sm:w-52 px-3 py-2 rounded-lg bg-white/15 border border-white/25 text-white text-sm placeholder:text-white/50 focus:outline-none focus:border-white/50"
              />
              <button
                type="submit"
                disabled={giveawayLoading}
                className="px-4 py-2 bg-white text-blue-700 font-bold text-sm rounded-lg hover:bg-blue-50 transition-colors flex-shrink-0 disabled:opacity-60"
              >
                {giveawayLoading ? '...' : 'Join'}
              </button>
            </form>
          </div>
        </motion.div>
      )}
      {giveawayClaimed && (
        <div className="bg-green-600/90 text-white text-center py-3 text-sm font-semibold">
          ✅ You're in! Check your email for a welcome surprise. <button onClick={() => navigate('/audit')} className="underline ml-1">Start your free audit →</button>
        </div>
      )}

      {/* ── NEW ARRIVALS MARQUEE ─────────────────────────────────────────────── */}
      {/* Round-17.2: Sarah dental verdict shipped 9/10. User asked to
          hold off on the new-arrivals widget until the catalog has
          more reliable weekly tool inflows — when only 1 tool is
          added in a week, the marquee 4x-duplicates it visually
          (R17.1 backend fallback helped but the widget still feels
          off when "this week" only has 1-2 real entries). Re-enable
          by removing this `false &&` once data_ingestor + tool_promoter
          jobs are reliably adding 5+ tools/week. */}
      {false && newArrivals.length > 0 && (
        <section className="pt-24 pb-14 px-0 bg-[#0f172a] border-b border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 px-4"
          >
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-[#3b82f6] text-xs font-semibold px-4 py-1.5 rounded-full mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3b82f6] animate-pulse" />
              {t("home.newArrivalsBadge")}
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-white font-['Space_Grotesk'] tracking-tight">
              {t("home.newArrivalsTitle")}
            </h2>
            <p className="text-slate-500 text-sm mt-2">{t("home.newArrivalsSubtitle")}</p>
          </motion.div>

          <div className="marquee-container space-y-3 overflow-hidden">
            {/* Row 1 — left */}
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-[#0f172a] to-transparent pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-[#0f172a] to-transparent pointer-events-none" />
              <div className="marquee-row flex gap-4 w-max" style={{ animation: "marquee-left 35s linear infinite" }}>
                {[...marqueeRow1, ...marqueeRow1, ...marqueeRow1, ...marqueeRow1].map((tool, i) => (
                  <div key={`r1-${tool.id}-${i}`} className="flex-shrink-0 bg-slate-900/60 backdrop-blur-sm border border-white/8 rounded-2xl px-5 py-4 w-64 hover:border-blue-500/20 hover:bg-slate-900/80 transition-all duration-300 cursor-default">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="text-sm font-bold text-white leading-tight">{tool.name}</span>
                      {tool.adoption_trend === "emerging" ? (
                        <span className="flex-shrink-0 text-[10px] font-semibold bg-purple-500/15 text-purple-400 border border-purple-500/25 px-2 py-0.5 rounded-full">{t("home.trendEmerging")}</span>
                      ) : tool.adoption_trend === "growing" ? (
                        <span className="flex-shrink-0 text-[10px] font-semibold bg-blue-500/15 text-[#3b82f6] border border-blue-500/25 px-2 py-0.5 rounded-full">{t("home.trendGrowing")}</span>
                      ) : null}
                    </div>
                    <div className="text-[10px] font-medium text-slate-500 mb-2">{tool.category}</div>
                    {tool.notes && <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{tool.notes}</p>}
                    <div className="mt-3 text-[10px] text-slate-600">
                      {arrivalDateCounts[tool.date_added] === 1
                        ? `${t("home.newArrivalsAdded")} ${new Date(tool.date_added).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                        : tool.pricing_band || t("home.newArrivalsRecent")}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 2 — right */}
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-[#0f172a] to-transparent pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-[#0f172a] to-transparent pointer-events-none" />
              <div className="marquee-row flex gap-4 w-max" style={{ animation: "marquee-right 42s linear infinite" }}>
                {[...marqueeRow2, ...marqueeRow2, ...marqueeRow2, ...marqueeRow2].map((tool, i) => (
                  <div key={`r2-${tool.id}-${i}`} className="flex-shrink-0 bg-slate-900/60 backdrop-blur-sm border border-white/8 rounded-2xl px-5 py-4 w-64 hover:border-blue-500/20 hover:bg-slate-900/80 transition-all duration-300 cursor-default">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="text-sm font-bold text-white leading-tight">{tool.name}</span>
                      {tool.adoption_trend === "emerging" ? (
                        <span className="flex-shrink-0 text-[10px] font-semibold bg-purple-500/15 text-purple-400 border border-purple-500/25 px-2 py-0.5 rounded-full">{t("home.trendEmerging")}</span>
                      ) : tool.adoption_trend === "growing" ? (
                        <span className="flex-shrink-0 text-[10px] font-semibold bg-blue-500/15 text-[#3b82f6] border border-blue-500/25 px-2 py-0.5 rounded-full">{t("home.trendGrowing")}</span>
                      ) : null}
                    </div>
                    <div className="text-[10px] font-medium text-slate-500 mb-2">{tool.category}</div>
                    {tool.notes && <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{tool.notes}</p>}
                    <div className="mt-3 text-[10px] text-slate-600">
                      {arrivalDateCounts[tool.date_added] === 1
                        ? `${t("home.newArrivalsAdded")} ${new Date(tool.date_added).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                        : tool.pricing_band || t("home.newArrivalsRecent")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <style>{`
            @keyframes marquee-left  { 0% { transform: translateX(0); }    100% { transform: translateX(-25%); } }
            @keyframes marquee-right { 0% { transform: translateX(-25%); } 100% { transform: translateX(0); }    }
            .marquee-container:hover .marquee-row { animation-play-state: paused; }
          `}</style>

          <div className="text-center mt-8 px-4">
            <button onClick={() => navigate("/audit")} className="text-sm text-slate-500 hover:text-[#3b82f6] transition-colors inline-flex items-center gap-1.5">
              {t("home.newArrivalsCtaLink")} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative pt-24 pb-28 px-4 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] bg-blue-500/8 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.018]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT: Copy */}
            <div>
              {/* Social proof pill */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex items-center gap-3 mb-8">
                <div className="flex -space-x-2">
                  {SOCIAL_PROOF_AVATARS.map(({ initials, bg }) => (
                    <div key={initials} className={`w-8 h-8 rounded-full border-2 border-[#0f172a] flex items-center justify-center text-[10px] font-bold text-white ${bg}`}>{initials}</div>
                  ))}
                </div>
                <div className="flex items-center gap-2 bg-slate-900/70 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-3 h-3 text-yellow-400" fill="currentColor" />)}
                  </div>
                  <span className="text-slate-400 text-xs">{t("home.socialProofText")}</span>
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h1
                id="hero-heading"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-[1.05] font-['Space_Grotesk']"
              >
                <span className="text-white">{t("home.heroTitleLine1")}</span>
                <br />
                <span className="bg-gradient-to-r from-[#3b82f6] via-teal-400 to-blue-400 bg-clip-text text-transparent">
                  {t("home.heroTitleLine2")}
                </span>
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-lg text-slate-300 mb-8 leading-relaxed max-w-xl">
                {t("home.heroSubtitle", { toolCount: displayCount })}
              </motion.p>

              {/* Benefits */}
              <motion.ul initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="space-y-2.5 mb-10">
                {[t("home.heroBullet1"), t("home.heroBullet2"), t("home.heroBullet3")].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </motion.ul>

              {/* CTAs */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="flex flex-wrap gap-4 mb-6">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => { track("cta_click", { page: "homepage", button: "hero_start_audit" }); navigate("/audit"); }}
                  className="relative bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-black px-8 py-4 text-base rounded-full shadow-2xl shadow-blue-500/30 hover:shadow-blue-500/50 transition-all"
                >
                  <span className="absolute inset-0 rounded-full bg-[#3b82f6]/20 blur-md animate-pulse" />
                  <span className="relative flex items-center gap-2">
                    {t("home.heroPrimaryCtaPrice", { price: `$${currentPrice}` })} <ArrowRight className="w-5 h-5" />
                  </span>
                </motion.button>
                {process.env.REACT_APP_SAMPLE_PDF_URL && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    onClick={() => window.open(process.env.REACT_APP_SAMPLE_PDF_URL, "_blank")}
                    className="flex items-center gap-2 bg-slate-900/70 backdrop-blur-sm border border-white/10 text-slate-300 hover:text-white hover:border-white/20 px-8 py-4 text-base rounded-full transition-all"
                  >
                    <Play className="w-4 h-4" />
                    {t("home.heroSecondaryCta")}
                  </motion.button>
                )}
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex flex-wrap items-center gap-4 text-xs text-slate-600 mb-6">
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> <span>{t("home.heroSecureLabel")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <img src="/logos/visa.svg" alt="Visa" className="h-3 w-auto opacity-40" />
                  <img src="/logos/mastercard.svg" alt="Mastercard" className="h-3 w-auto opacity-40" />
                  <img src="/logos/amex.svg" alt="Amex" className="h-3 w-auto opacity-40" />
                </div>
              </motion.div>

              {/* Live ticker removed — no fabricated activity */}
            </div>

            {/* RIGHT: Blueprint Preview */}
            <motion.div
              initial={{ opacity: 0, x: 40, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.7, ease: "easeOut" }}
              className="hidden lg:block"
            >
              <BlueprintPreview t={t} />
            </motion.div>
          </div>

          {/* Industries strip */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-20 pt-10 border-t border-white/5 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-600 mb-6 font-semibold">{t("home.industriesStripLabel")}</p>
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
              {[
                { icon: Code,         label: t("home.industrySaaS"),         link: "/saas" },
                { icon: ShoppingCart, label: t("home.industryEcom"),       link: "/ecommerce" },
                { icon: Utensils,     label: t("home.industryRestaurants"), link: "/restaurant" },
                { icon: Stethoscope,  label: t("home.industryHealthcare"),  link: "/healthcare" },
                { icon: HomeIcon,     label: t("home.industryRealEstate"),  link: "/real-estate" },
                { icon: Brain,        label: t("home.industryProfServices"), link: "/professional-services" },
              ].map(({ icon: Icon, label, link }) => (
                <div key={label} className={`flex items-center gap-2 text-slate-600 hover:text-slate-300 transition-colors text-sm ${link ? "cursor-pointer" : "cursor-default"}`} onClick={() => link && navigate(link)}>
                  <Icon className="w-4 h-4" /> <span>{label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────────────────────── */}
      <section className="py-12 px-4 bg-slate-950/60 border-y border-white/5">
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {STATS.map((s) => (
            <motion.div key={s.id} variants={fadeUp} className="text-center">
              <div className={`text-4xl md:text-5xl font-black mb-1 font-['Space_Grotesk'] ${s.color}`}>{s.value}</div>
              <div className="text-slate-500 text-xs">{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
              <Zap className="w-3.5 h-3.5" /> {t("home.howBadge")}
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-4 font-['Space_Grotesk'] tracking-tight">{t("home.howTitle")}</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">{t("home.howSubtitle")}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 relative">
            <div className="hidden md:block absolute top-14 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-px bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-blue-500/30" />
            {[
              { num: 1, title: t("home.howStep1Title"), desc: t("home.howStep1Desc"), time: t("home.howStep1Time"), color: "text-blue-400",    num_bg: "bg-gradient-to-br from-blue-500 to-blue-600"     },
              { num: 2, title: t("home.howStep2Title"), desc: t("home.howStep2Desc"), time: t("home.howStep2Time"), color: "text-purple-400",  num_bg: "bg-gradient-to-br from-purple-500 to-purple-600"  },
              { num: 3, title: t("home.howStep3Title"), desc: t("home.howStep3Desc"), time: t("home.howStep3Time"), color: "text-[#3b82f6]", num_bg: "bg-gradient-to-br from-blue-500 to-blue-500" },
            ].map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="bg-slate-900/70 backdrop-blur-sm border border-white/8 rounded-3xl p-8 text-center hover:border-white/15 transition-all group"
              >
                <div className={`w-14 h-14 ${step.num_bg} rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-black text-white shadow-lg`}>
                  {step.num}
                </div>
                <div className={`text-xs font-semibold uppercase tracking-[0.15em] ${step.color} mb-2`}>{step.time}</div>
                <h3 className="text-lg font-bold text-white mb-2 font-['Space_Grotesk']">{step.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="mt-10 text-center">
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} onClick={() => navigate("/audit")} className="bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-black px-10 py-4 rounded-full shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transition-all text-base inline-flex items-center gap-2">
              {t("home.heroPrimaryCta")} <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT YOU GET ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-4 bg-slate-950/40">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
              {t("home.featuresBadge")}
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-4 font-['Space_Grotesk'] tracking-tight">{t("home.featuresTitle")}</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">{t("home.featuresSubtitle")}</p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_YOU_GET.map((f) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  variants={fadeUp}
                  whileHover={{ y: -4, transition: { type: "spring", stiffness: 400 } }}
                  className={`relative bg-slate-900/70 backdrop-blur-sm border ${f.featured ? "border-purple-500/30" : "border-white/8"} rounded-3xl p-6 hover:border-white/15 transition-all overflow-hidden group`}
                >
                  {f.featured && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent rounded-3xl" />
                      <span className="absolute -top-3 left-6 text-[10px] font-bold bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-1 rounded-full shadow-lg">
                        ⭐ {t("home.featuredMostValued")}
                      </span>
                    </>
                  )}
                  <div className={`relative w-11 h-11 bg-gradient-to-br ${f.glow} to-transparent rounded-2xl flex items-center justify-center mb-4 border border-white/10`}>
                    <Icon className={`w-5 h-5 ${f.color}`} />
                  </div>
                  <h3 className="relative text-base font-bold text-white mb-2 leading-snug font-['Space_Grotesk']">{f.title}</h3>
                  <p className="relative text-slate-400 text-sm leading-relaxed mb-4">{f.desc}</p>
                  <span className={`relative inline-block text-xs bg-white/8 border ${f.border} ${f.color} px-3 py-1 rounded-full font-medium`}>{f.stat}</span>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-12">
            <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate("/audit")} className="bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold px-10 py-4 rounded-full shadow-lg hover:shadow-purple-500/20 text-base inline-flex items-center gap-2 transition-all">
              {t("home.featuresCta")} <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── EXPLORE FREE TOOLS ──────────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/80 via-slate-950/70 to-slate-900/80 backdrop-blur-xl p-10 md:p-14">
            <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/8 rounded-full blur-[100px] pointer-events-none" />
            <div className="relative flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-[#3b82f6] text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                  <Search className="w-3 h-3" /> {t("home.discoverBadge") || "Free to explore"}
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-3 font-['Space_Grotesk'] tracking-tight">
                  {t("home.discoverTitle") || "Browse Our AI Tool Catalog"}
                </h2>
                <p className="text-slate-400 leading-relaxed mb-6">
                  {t("home.discoverDesc") || `Explore ${displayCount}+ vetted AI tools, see this week's top pick from our engine, and build a sample stack — all free. When you're ready for the full personalized blueprint, we'll be here.`}
                </p>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate("/discover")}
                  className="inline-flex items-center gap-2 bg-white/8 border border-white/15 text-white font-semibold px-6 py-3 rounded-full hover:bg-white/12 hover:border-white/25 transition-all text-sm"
                >
                  {t("home.discoverCta") || "Explore Tools"} <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
              <div className="md:flex-shrink-0 grid grid-cols-2 gap-3 w-full md:w-48">
                {[
                  { val: `${displayCount}+`, lab: t("home.discoverStatTools") || "AI tools" },
                  { val: "12", lab: t("home.discoverStatIndustries") || "Industries" },
                  { val: "Weekly", lab: t("home.discoverStatPick") || "Engine pick" },
                  { val: "Free", lab: t("home.discoverStatPrice") || "To browse" },
                ].map(({ val, lab }) => (
                  <div key={lab} className="bg-slate-950/50 border border-white/8 rounded-xl p-3 text-center min-w-0">
                    <div className="text-base sm:text-lg font-black text-[#3b82f6] font-['Space_Grotesk'] truncate">{val}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 truncate">{lab}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SIGNAL INBOX ───────────────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl border border-purple-500/20 bg-gradient-to-br from-slate-900/80 via-[#120e2a]/80 to-slate-900/80 backdrop-blur-xl p-10 md:p-14"
          >
            <div className="absolute top-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-500/8 rounded-full blur-[100px] pointer-events-none" />
            <div className="relative flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/25 text-purple-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                  <Radar className="w-3 h-3" /> New — monthly SaaS
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-3 font-['Space_Grotesk'] tracking-tight">
                  Meet Signal Inbox
                </h2>
                <p className="text-slate-300 leading-relaxed mb-4 text-base">
                  AI that scans Reddit 24/7 and finds the exact business owners
                  asking about the problems <span className="text-white font-semibold">your</span> business solves.
                </p>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  Each match gets a pre-drafted, non-spammy reply built from our 300+ tool database —
                  ready for you to approve with one click. No cold DMs, no "hey fellow founder."
                  Just qualified leads who already raised their hand.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate("/signal-inbox")}
                    className="inline-flex items-center gap-2 bg-gradient-to-br from-purple-500 to-blue-500 text-white font-bold px-6 py-3 rounded-full shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all text-sm"
                  >
                    See Signal Inbox <ArrowRight className="w-4 h-4" />
                  </motion.button>
                  <span className="text-xs text-slate-500">
                    Starts at <span className="text-white font-semibold">$79/mo</span> · 30-day free trial with any blueprint
                  </span>
                </div>
              </div>
              <div className="md:flex-shrink-0 grid grid-cols-2 gap-3 w-full md:w-56">
                {[
                  { val: "24/7", lab: "Reddit scan" },
                  { val: "1-click", lab: "Approval" },
                  { val: "300+", lab: "Tool matches" },
                  { val: "$79/mo", lab: "Entry tier" },
                ].map(({ val, lab }) => (
                  <div key={lab} className="bg-slate-950/60 border border-purple-500/15 rounded-xl p-3 text-center min-w-0">
                    <div className="text-base sm:text-lg font-black text-purple-300 font-['Space_Grotesk'] truncate">{val}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 truncate">{lab}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TRUST / EARLY STAGE ────────────────────────────────────────── */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px flex-1 bg-white/8" />
            <Shield className="w-4 h-4 text-slate-600" />
            <div className="h-px flex-1 bg-white/8" />
          </div>
          <p className="text-slate-500 text-sm mb-2">
            {t("home.trustLine1") || "We're a new company building something we believe in."}
          </p>
          <p className="text-slate-600 text-xs">
            {t("home.trustLine2") || "60-Day Implementation Guarantee — follow Phase 1 or full refund."}
          </p>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────────── */}
      <section className="py-24 px-4 bg-slate-950/40">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-black mb-3 font-['Space_Grotesk'] tracking-tight">{t("home.pricingTitle")}</h2>
            <p className="text-slate-400 text-lg">{t("home.pricingSubtitle")}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-10 items-center max-w-3xl mx-auto">
            {/* Price card */}
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-br from-blue-500/50 via-blue-500/30 to-purple-500/50 rounded-[2rem] blur-sm" />
              <div className="relative bg-[#0f172a] border border-white/10 rounded-3xl p-8 shadow-2xl">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-gradient-to-r from-blue-500 to-teal-500 text-slate-950 font-bold text-sm px-5 py-1.5 rounded-full shadow-lg shadow-blue-500/30 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> {t("home.pricingMostPopular")}
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mt-4 mb-1">
                  <span className="text-5xl sm:text-6xl font-black text-white font-['Space_Grotesk']">${currentPrice}</span>
                  <span className="text-sm text-blue-400 font-semibold ml-2">{t("home.pricingSavings")}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {PRICING_ITEMS.map((key) => (
                    <li key={key} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                      <span>{t(`home.${key}`)}</span>
                    </li>
                  ))}
                </ul>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { track("cta_click", { page: "homepage", button: "pricing_get_blueprint" }); navigate("/audit"); }}
                  className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-black py-4 rounded-2xl shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 text-base transition-all flex items-center justify-center gap-2"
                >
                  {t("home.pricingCta")} <ArrowRight className="w-5 h-5" />
                </motion.button>
                <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-600">
                  <Lock className="w-3 h-3" /> <span>{t("home.pricingSecure")}</span>
                </div>
              </div>
            </motion.div>

            {/* What's included */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-5">
              <p className="text-slate-500 text-xs uppercase tracking-[0.2em] font-semibold mb-6">{t("home.pricingIncludedLabel")}</p>
              {[
                { icon: "🗺️", title: t("home.pricingInc1Title"), sub: t("home.pricingInc1Sub") },
                { icon: "🛠️", title: t("home.pricingInc2Title", { toolCount: displayCount }), sub: t("home.pricingInc2Sub") },
                { icon: "💬", title: t("home.pricingInc3Title"), sub: t("home.pricingInc3Sub") },
                { icon: "📈", title: t("home.pricingInc4Title"), sub: t("home.pricingInc4Sub") },
                { icon: "⚠️", title: t("home.pricingInc5Title"), sub: t("home.pricingInc5Sub") },
                { icon: "📄", title: t("home.pricingInc6Title"), sub: t("home.pricingInc6Sub") },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <span className="text-xl flex-shrink-0">{item.icon}</span>
                  <div>
                    <div className="text-white font-semibold text-sm">{item.title}</div>
                    <div className="text-slate-500 text-xs mt-0.5">{item.sub}</div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── COMPARISON ───────────────────────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black mb-3 font-['Space_Grotesk'] tracking-tight">{t("home.cmpTitle")}</h2>
            <p className="text-slate-400">{t("home.cmpSubtitle")}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overflow-x-auto rounded-3xl border border-white/8 bg-slate-900/60 backdrop-blur-sm">
            <table className="w-full text-sm border-collapse" aria-label="Product comparison">
              <thead>
                <tr className="border-b border-white/8">
                  <th className="text-left text-slate-500 font-medium py-4 px-6 w-1/2" scope="col">{t("home.cmpColFeature")}</th>
                  <th className="text-center py-4 px-4 w-[18%]" scope="col">
                    <div className="flex flex-col items-center gap-1">
                      <span className="bg-gradient-to-r from-blue-500 to-teal-500 text-slate-950 text-xs font-bold px-3 py-1 rounded-full">AI Workshop</span>
                      <span className="text-[#3b82f6] font-black text-base font-['Space_Grotesk']">${currentPrice}</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-4 w-[18%]" scope="col">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-slate-500 text-xs">{t("home.cmpColConsultant")}</span>
                      <span className="text-slate-600 font-bold text-base">$5k+</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-4 w-[14%]" scope="col">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-slate-500 text-xs">{t("home.cmpColDiy")}</span>
                      <span className="text-slate-600 font-bold text-base">∞ hrs</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr key={i} className="border-b last:border-0 border-white/5 hover:bg-white/2 transition-colors">
                    <td className={`py-3.5 px-6 ${i === COMPARISON_ROWS.length - 1 ? "font-bold text-white" : "text-slate-300"}`}>{row.feature}</td>
                    <td className="py-3.5 px-4 text-center">
                      {row.us === true ? <CheckCircle className="w-5 h-5 text-[#3b82f6] mx-auto" /> : <span className="font-black text-[#3b82f6] font-['Space_Grotesk']">{row.us === null ? `$${currentPrice}` : row.us}</span>}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      {row.consultant === true ? <CheckCircle className="w-5 h-5 text-slate-600 mx-auto" /> : row.consultant === false ? <X className="w-5 h-5 text-red-500/40 mx-auto" /> : <span className="text-slate-500 text-xs">{row.consultant}</span>}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      {row.diy === true ? <CheckCircle className="w-5 h-5 text-slate-600 mx-auto" /> : row.diy === false ? <X className="w-5 h-5 text-red-500/40 mx-auto" /> : <span className="text-slate-500 text-xs font-medium">{row.diy}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="text-center mt-8">
            <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate("/audit")} className="bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold px-8 py-4 rounded-full shadow-lg text-base inline-flex items-center gap-2 transition-all">
              {t("home.cmpCta", { price: `$${currentPrice}` })} <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ── FOUNDER ──────────────────────────────────────────────────────────── */}
      <section className="py-20 px-4 bg-slate-950/40">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900/70 backdrop-blur-xl border border-white/8 rounded-3xl p-10 md:p-14 flex flex-col md:flex-row gap-8 items-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/3 to-transparent rounded-3xl" />
            <div className="relative flex-shrink-0">
              <div className="w-24 h-24 bg-gradient-to-br from-[#3b82f6] to-teal-600 rounded-3xl flex items-center justify-center text-3xl font-black text-slate-950 shadow-2xl shadow-blue-500/20 font-['Space_Grotesk']">
                AE
              </div>
            </div>
            <div className="relative">
              <blockquote className="text-xl leading-relaxed text-slate-200 mb-6 italic">
                "{t("home.founderQuote")}"
              </blockquote>
              <div className="font-bold text-white text-base font-['Space_Grotesk']">{t("home.founderName")}</div>
              <div className="text-slate-400 text-sm mb-4">{t("home.founderRole")}</div>
              <button
                onClick={() => window.open("https://calendly.com/ali-aiworkshop/chat", "_blank")}
                className="text-[#3b82f6] hover:text-blue-300 text-sm font-semibold inline-flex items-center gap-1 transition-colors"
              >
                {t("home.founderCta")} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────────── */}
      <section className="py-28 px-4 relative overflow-hidden text-center bg-[#070d1f]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]" />
          <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>
        <div className="max-w-3xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2 text-sm text-slate-300 mb-8">
              <span className="w-2 h-2 rounded-full bg-[#3b82f6] animate-pulse" />
              {t("home.finalCtaEngineLive", { count: toolCount })}
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-5 leading-tight font-['Space_Grotesk'] tracking-tight">
              {t("home.finalCtaTitle")}
            </h2>
            <p className="text-slate-400 text-xl mb-12">{t("home.finalCtaSubtitle")}</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/audit")}
              className="bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 text-xl px-14 py-6 rounded-full font-black shadow-2xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all mb-10 inline-flex items-center gap-3"
            >
              {t("home.finalCtaButton")} <ArrowRight className="w-6 h-6" />
            </motion.button>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500 mb-16">
              {[t("home.finalCtaCheck1"), t("home.finalCtaCheck2", { price: `$${currentPrice}` }), t("home.finalCtaCheck3")].map((item) => (
                <div key={item} className="flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-[#3b82f6]" /> {item}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Subscribe */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="border-t border-white/8 pt-12">
            <p className="text-slate-300 font-semibold mb-1">{t("home.subscribeTitle")}</p>
            <p className="text-slate-500 text-sm mb-6">{t("home.subscribeSubtitle")}</p>
            <form onSubmit={handleSubscribe} className="flex flex-wrap justify-center gap-3">
              <label htmlFor="subscribe-email" className="sr-only">{t("home.subscribeEmailLabel")}</label>
              <Input
                id="subscribe-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("home.subscribePlaceholder")}
                className="min-w-[240px] bg-white/5 border-white/10 text-white placeholder:text-slate-600 rounded-2xl"
              />
              <button type="submit" className="bg-blue-500 hover:bg-blue-500 text-white px-6 rounded-2xl font-semibold py-2 transition-colors">
                {t("home.subscribeButton")}
              </button>
            </form>
            <p className="text-xs text-slate-700 mt-3">{t("home.subscribeFinePrint")}</p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
